import { cn, money, pct } from "./api";

export type Extreme = { assets: number; profit: number; actions: string[] };
export type Placed = {
  id: string;
  name: string;
  assets: number;
  rank: number;
  me?: boolean;
};
const START = 100_000_000;

// Final assets placed between the worst and best possible run. The track only
// carries dots; names, amounts and percentages sit in a list below so nothing
// overlaps however the players are spread.
export function PositionTrack({
  best,
  worst,
  players,
  note,
}: {
  best: Extreme;
  worst: Extreme;
  players: Placed[];
  note?: string;
}) {
  const span = best.assets - worst.assets;
  const at = (assets: number) =>
    span > 0 ? (assets - worst.assets) / span : 0.5;
  const ranked = [...players].sort(
    (a, b) => a.rank - b.rank || a.name.localeCompare(b.name),
  );
  // Dots have a fixed width (~6% of the narrowest track), so neighbours
  // closer than 8% share one dot that shows how many players it holds.
  const clusters: Placed[][] = [];
  for (const p of [...ranked].sort((a, b) => a.assets - b.assets)) {
    const last = clusters.at(-1);
    if (last && at(p.assets) - at(last.at(-1)!.assets) < 0.08) last.push(p);
    else clusters.push([p]);
  }
  return (
    <div className="position">
      <div className="position-edges">
        <span className="is-worst">
          <small>이번 판 최저 성적</small>
          <strong>{money(worst.assets)}</strong>
          <b className={cn(worst.profit)}>
            {pct((worst.profit / START) * 100)}
          </b>
        </span>
        <span className="is-best">
          <small>이번 판 최고 성적</small>
          <strong>{money(best.assets)}</strong>
          <b className={cn(best.profit)}>{pct((best.profit / START) * 100)}</b>
        </span>
      </div>
      <div className="position-track" aria-hidden="true">
        {clusters.map((group) => (
          <span
            key={group[0].id}
            className={`position-marker ${group.some((p) => p.me) ? "is-me" : ""}`}
            style={{
              left: `${(group.reduce((sum, p) => sum + at(p.assets), 0) / group.length) * 100}%`,
            }}
          >
            {group.length > 1 ? group.length : ""}
          </span>
        ))}
      </div>
      <ol className="position-list" aria-label="최저와 최고 사이 위치">
        {ranked.map((p) => (
          <li key={p.id} className={p.me ? "is-me" : ""}>
            <span className="position-rank">{p.rank}</span>
            <strong>
              {p.name}
              {p.me && p.name !== "나" && " · 나"}
            </strong>
            <span>{money(p.assets)}</span>
            <b>{Math.round(at(p.assets) * 100)}%</b>
          </li>
        ))}
      </ol>
      <p className="caption">
        최고 성적은 5문제 모두 최고의 선택을, 최저 성적은 모두 최악의 선택을
        했을 때의 최종 자산이고, %는 최저(0%)와 최고(100%) 사이의 위치예요.
        {span === 0 && " 이번 판은 어떤 선택을 해도 결과가 같았어요."}
        {note && ` ${note}`}
      </p>
    </div>
  );
}
